/**
 * CrmUserFilter - Filtro de usuário responsável para atividades
 * 
 * Permite selecionar um usuário (quem executou a atividade) ou "Todos".
 * Baseado no estilo do CrmSellerFilter para consistência visual.
 * 
 * @author Sistema
 * @version 1.0.0
 * RELEASE: 2026-10-05 v1.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 */

import React, { useState } from 'react';
import { Check, ChevronDown, UserCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Props do componente CrmUserFilter
 * @param value - ID do usuário selecionado (null = todos)
 * @param onChange - Callback para mudança de seleção
 * @param className - Classes CSS adicionais
 */
interface CrmUserFilterProps {
  value: string | null;
  onChange: (userId: string | null) => void;
  className?: string;
  /** Rótulo exibido quando nenhum usuário selecionado (padrão: "Responsável") */
  label?: string;
  /** Texto do placeholder de busca (padrão: "Buscar responsável...") */
  searchPlaceholder?: string;
  /** Texto do item "Todos" na lista (padrão: "Todos Responsáveis") */
  allLabel?: string;
  /** Título do grupo na lista (padrão: "Responsáveis") */
  groupLabel?: string;
}

/**
 * Tipo para membro do tenant (usuário)
 */
interface TenantMember {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  email: string | null;
}

/**
 * Componente de filtro de usuário responsável
 */
export function CrmUserFilter({
  value,
  onChange,
  className,
  label = 'Responsável',
  searchPlaceholder = 'Buscar responsável...',
  allLabel = 'Todos Responsáveis',
  groupLabel = 'Responsáveis',
}: CrmUserFilterProps) {
  const { currentTenant } = useAuth();
  const [open, setOpen] = useState(false);

  // v1.1.0: Migrado para hook unificado `useTenantMembers` (RPC server-side).
  // Substitui SELECT direto em tenant_members + JOIN com profiles, eliminando
  // requisições repetidas a tenant_members?tenant_id=eq.<uuid> e compartilhando
  // o cache (queryKey ['tenant-members', tenantId, true]) com outros pickers.
  const { data: rawMembers = [], isLoading } = useTenantMembers({ onlyActive: true });
  const members: TenantMember[] = rawMembers.map((m) => ({
    id: m.user_id,
    full_name: m.full_name,
    avatar_url: m.avatar_url,
    email: m.email,
  }));

  // Usuário selecionado
  const selectedMember = members.find(m => m.id === value);

  // Handler para seleção
  const handleSelect = (userId: string | null) => {
    onChange(userId);
    setOpen(false);
  };

  // Detecta se é versão compacta baseado na classe
  const isCompact = className?.includes('h-[22px]');

  if (isLoading) {
    return <Skeleton className={cn(isCompact ? 'h-[22px] w-[100px]' : 'h-9 w-[160px]')} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 px-3 gap-2 font-normal border-0 bg-background rounded-full',
            className
          )}
        >
          {selectedMember ? (
            <>
              <SmartAvatar
                src={selectedMember.avatar_url}
                name={selectedMember.full_name || 'Usuário'}
                size="xs"
                use3DFallback
                className={isCompact ? 'h-4 w-4' : undefined}
              />
              <span className={cn('truncate hidden sm:inline', isCompact ? 'max-w-[60px]' : 'max-w-[100px]')}>
                {selectedMember.full_name || 'Usuário'}
              </span>
            </>
          ) : (
            <>
              <UserCheck className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
              <span className="hidden sm:inline truncate">{label}</span>
            </>
          )}
          <ChevronDown className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'opacity-50')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => handleSelect(null)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                    value === null
                      ? "border-primary bg-primary"
                      : "border-muted"
                  )}
                >
                  {value === null && (
                    <Check className="h-2.5 w-2.5 text-primary-foreground" />
                  )}
                </div>
                <UserCheck className="h-4 w-4 mr-2 opacity-50" />
                <span>{allLabel}</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading={groupLabel}>
              {members.map(member => (
                <CommandItem
                  key={member.id}
                  value={member.full_name || member.email || member.id}
                  onSelect={() => handleSelect(member.id)}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                      value === member.id
                        ? "border-primary bg-primary"
                        : "border-muted"
                    )}
                  >
                    {value === member.id && (
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    )}
                  </div>
                  <SmartAvatar
                    src={member.avatar_url}
                    name={member.full_name || 'Usuário'}
                    size="sm"
                    use3DFallback
                    className="mr-2"
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="truncate">{member.full_name || 'Usuário'}</span>
                    {member.email && (
                      <span className="text-xs text-muted-foreground truncate">
                        {member.email}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
